import { PaymentWidget } from "./PaymentWidget";

export class RobokassaWidget extends PaymentWidget {
    filesUrl = null;
    AccUrl = null;
    urlCheckInterval = null;
    iframe = null;
    lastKnownUrl = "";
    allowedOrigins = ["https://assistant.demo.moneta.ru"];
    boundHandleMessage = null;
    paymentCompleted = false;
    initialLoadCompleted = false;
    paymentError = null;
    monitoringActive = false;

    init() {
        console.log("RobokassaWidget initialized");
        try {
            this.iframe = this.find("iframe");
            if (!this.iframe) {
                throw new Error("Payment iframe not found");
            }

            this.filesUrl = this.iframe.getAttribute("data-files-url");
            this.AccUrl = this.iframe.getAttribute("data-account-url");
            super.init();

            this.hideBackToMerchant();

            this.iframe.onload = () => {
                this.initialLoadCompleted = true;
                console.log("Iframe initial load completed");

                if (this.paymentError) {
                    this.handlePaymentError(this.paymentError);
                }
            };

            this.iframe.onerror = () => {
                this.paymentError = "Failed to load payment iframe";
                console.error("Iframe loading error");
                if (this.initialLoadCompleted) {
                    this.handlePaymentError(this.paymentError);
                }
            };

            this.setupIframeMonitoring();
            this.boundHandleMessage = this.handleMessage.bind(this);
            window.addEventListener("message", this.boundHandleMessage);
        } catch (error) {
            console.error("Initialization error:", error);
            this.error({
                message: "Payment widget initialization failed",
                details: error.message,
                code: "INIT_FAILED",
            });
        }
    }

    setupIframeMonitoring() {
        setTimeout(() => {
            if (!this.initialLoadCompleted) {
                this.paymentError.message = "Payment iframe loading timeout";
                this.handlePaymentError(this.paymentError);
                return;
            }

            this.monitoringActive = true;
            this.tryGetIframeUrl();
            this.urlCheckInterval = setInterval(() => {
                this.tryGetIframeUrl();
            }, 1000);
        }, 2000);
    }

    hideBackToMerchant() {
        const backToMerchantById = document.getElementById("backToMerchant");
        if (backToMerchantById) {
            backToMerchantById.style.display = "none";
        }
    }

    tryGetIframeUrl() {
        if (!this.monitoringActive || !this.initialLoadCompleted) {
            return;
        }

        try {
            const iframeUrl = this.iframe.contentWindow?.location.href;
            if (iframeUrl && iframeUrl !== this.lastKnownUrl) {
                this.lastKnownUrl = iframeUrl;
                this.checkIframeUrl(iframeUrl);
            }
        } catch (error) {
            if (this.iframe.src !== this.lastKnownUrl) {
                this.lastKnownUrl = this.iframe.src;
                this.checkIframeUrl(this.iframe.src);
            }
        }
    }

    checkIframeUrl(url) {
        if (!url || !this.monitoringActive || this.paymentCompleted) {
            return;
        }

        console.log("Checking iframe URL:", url);

        const successConditions = [
            /\/success(\/|$|\?)/i,
            /\/completed(\/|$|\?)/i,
            /(\?|&)result=success(&|$)/i,
            /(\?|&)status=completed(&|$)/i,
        ];

        const errorConditions = [
            /\/error(\/|$|\?)/i,
            /\/fail(\/|$|\?)/i,
            /\/cancel(\/|$|\?)/i,
            /\/rejected(\/|$|\?)/i,
        ];

        if (successConditions.some((regex) => regex.test(url))) {
            this.handlePaymentSuccess();
            return;
        }

        if (errorConditions.some((regex) => regex.test(url))) {
            this.handlePaymentError({
                message: "Payment failed",
                details: `Error URL detected: ${url}`,
                code: "PAYMENT_URL_ERROR",
            });
            return;
        }
    }

    handleMessage(event) {
        if (!this.allowedOrigins.includes(event.origin)) {
            return;
        }

        const data = event.data;

        if (this.paymentCompleted || !this.monitoringActive) {
            return;
        }

        try {
            switch (data?.type) {
                case "payment_error":
                    console.warn("Received payment_error message:", data);
                    this.handlePaymentError({
                        message: data.message || "Payment error",
                        details: data.details || "No additional details",
                        code: data.code || "PAYMENT_MESSAGE_ERROR",
                    });
                    break;

                case "payment_success":
                    console.log("Received payment_success message");
                    this.handlePaymentSuccess();
                    break;

                case "payment_status":
                    if (data.status === "success") {
                        console.log("Received payment_status success");
                        this.handlePaymentSuccess();
                    } else {
                        console.warn("Received payment_status failure:", data);
                        this.handlePaymentError({
                            message: data.message || "Payment failed",
                            details: "Payment status indicates failure",
                            code: data.code || "PAYMENT_STATUS_ERROR",
                        });
                    }
                    break;
            }
        } catch (error) {
            this.handlePaymentError({
                message: "Error processing payment message",
                details: error.message,
                code: "MESSAGE_PROCESSING_ERROR",
            });
        }
    }

    async handlePaymentSuccess() {
        if (this.paymentCompleted) {
            console.log("Payment already completed, ignoring success");
            return;
        }

        this.paymentCompleted = true;
        this.monitoringActive = false;

        clearInterval(this.urlCheckInterval);
        window.removeEventListener("message", this.boundHandleMessage);

        const data = {
            id: this.paymentData?.id,
            links: {
                account: this.AccUrl,
                files: this.filesUrl,
            },
        };

        try {
            await this.success(data);
        } catch (error) {
            this.error({
                message: "Error processing successful payment",
                details: error.message,
                code: "SUCCESS_HANDLER_ERROR",
            });
        }
    }

    handlePaymentError(errorInfo) {
        if (this.paymentCompleted) {
            return;
        }

        this.paymentCompleted = true;
        this.monitoringActive = false;

        clearInterval(this.urlCheckInterval);
        window.removeEventListener("message", this.boundHandleMessage);

        const errorData = {
            message: errorInfo.message || "Payment processing failed",
            details: errorInfo.details || "No additional details",
            code: errorInfo.code || "UNKNOWN_PAYMENT_ERROR",
        };

        console.error("Payment error occurred:", errorData);

        try {
            this.error(errorData);
        } catch (error) {
            console.error("Error in error handler:", error);
            alert(`Payment error: ${errorData.message}`);
        }
    }

    destroy() {
        this.monitoringActive = false;
        clearInterval(this.urlCheckInterval);
        window.removeEventListener("message", this.boundHandleMessage);
        this.iframe = null;
        console.log("RobokassaWidget destroyed");
    }
}
